import { PageTitle as SharedPageTitle } from '../components/layout/PageHeader';
import React from 'react';
import styled from 'styled-components';
import { Card } from '../components/ui/Card';
import { Icon } from '../components/ui/Icon';
import type { IconName } from '../components/ui/Icon';
import { Button } from '../components/ui/Button';
import { TermsModal } from '../components/modals/TermsModal';
import {
  CALCULATION_RULES,
  DATA_SOURCES,
  ELECTRICITY_FACTORS,
  GAS_FACTOR,
  INDICATORS,
  PRIVACY_ITEMS,
  SOURCE_LINKS,
  UPCOMING_ITEMS,
} from './transparencyContent';
import type { LabeledItem } from './transparencyContent';

// ============================================================================
// Styled Components
// ============================================================================

const PageContainer = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 56rem;
  margin: 0;
  padding: 0;
`;

const TransparencyContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  min-width: 0;
  margin: 0 auto;
`;

const PageHeader = styled.header`
  text-align: left;
  margin-bottom: 1.5rem;
`;

const PageTitle = styled(SharedPageTitle)``;

const PageSubtitle = styled.p`
  margin: 0.5rem 0 0;
  max-width: 65ch;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-muted);
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.25rem;
`;

const SectionCard = styled(Card)<{ $fullWidth?: boolean }>`
  min-width: 0;
  padding: 1.5rem;
`;

const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1rem;
  font-size: 1.125rem;
  font-weight: 600;
`;

const TitleIcon = styled.span`
  color: var(--brand-600);
  display: inline-flex;
`;

const SubTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: var(--brand-500);
  }
`;

const SectionContent = styled.div`
  color: var(--text-secondary);
  line-height: 1.65;
  font-size: 1rem;

  p {
    margin-bottom: 1rem;
  }

  p:last-child {
    margin-bottom: 0;
  }

  strong {
    color: var(--text-primary);
    font-weight: 600;
  }

  a {
    color: var(--brand-700);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const BulletList = styled.ul`
  margin: 1rem 0 0;
  padding: 0;
  list-style: none;

  li {
    position: relative;
    padding-left: 1.25rem;
    margin-bottom: 0.625rem;
  }

  li::before {
    content: '';
    position: absolute;
    left: 0.25rem;
    top: 0.65em;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--brand-500);
  }
`;

const DefinitionList = styled.dl`
  margin: 0;
  display: grid;
  gap: 1rem;

  div {
    padding-left: 0.875rem;
    border-left: 3px solid var(--brand-100, var(--border-soft));
  }

  dt {
    font-weight: 600;
    color: var(--text-primary);
  }

  dd {
    margin: 0.125rem 0 0;
    font-size: 0.9375rem;
  }
`;

const IndicatorGrid = styled.ul`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0;
  margin: 1.5rem 0 0;
  padding: 0;
  list-style: none;
`;

const IndicatorItem = styled.li`
  min-width: 0;
  padding: 1rem 0;
  border-bottom: 1px solid var(--border);

  h3 {
    margin-bottom: 0.5rem;
  }

  p {
    margin: 0.5rem 0 0;
    font-size: 0.875rem;
    color: var(--text-muted);
  }
`;

const Formula = styled.code`
  display: block;
  background: var(--surface-strong);
  padding: 0.5rem;
  border-radius: var(--radius-sm);
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.8125rem;
  color: var(--brand-700);
  word-break: break-word;
`;

const FactorsLayout = styled.div`
  display: grid;
  gap: 1.5rem;
  margin-top: 1.5rem;

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  }
`;

const FactorPanel = styled.div`
  padding: 1.25rem;
  background: var(--surface-strong);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-light);

  & + & {
    margin-top: 1rem;
  }

  p {
    font-size: 0.875rem;
  }
`;

const FactorValue = styled.span`
  background: var(--success-50);
  color: var(--success-600);
  padding: 0.25rem 0.5rem;
  border-radius: var(--radius-sm);
  font-weight: 600;
  font-size: 0.875rem;
  display: inline-block;
  margin-bottom: 0.75rem;
`;

const FactorTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  margin-bottom: 0.75rem;

  th,
  td {
    text-align: left;
    padding: 0.5rem 0.5rem;
    border-bottom: 1px solid var(--border-soft);
  }

  th {
    font-weight: 600;
    color: var(--text-primary);
  }

  td:nth-child(2) {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    color: var(--text-primary);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const Muted = styled.span`
  color: var(--text-muted);
  font-size: 0.8125rem;
`;

const SourceNote = styled.p`
  font-size: 0.8125rem !important;
  color: var(--text-muted);
  font-style: italic;
`;

const InfoBox = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background: var(--surface-strong);
  border-radius: var(--radius-md);
  border-left: 3px solid var(--brand-500);
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;

  svg {
    color: var(--brand-600);
    flex-shrink: 0;
    margin-top: 0.25rem;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
  }
`;

const IconList = styled.ul`
  display: grid;
  gap: 0.75rem;
  margin: 1rem 0 0;
  padding: 0;
  list-style: none;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));

  li {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  svg {
    color: var(--brand-500);
    flex-shrink: 0;
  }
`;

const FooterNote = styled.p`
  margin-top: 1.5rem !important;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-soft);
  text-align: center;
  color: var(--text-muted);
  font-size: 0.875rem;
`;

// ============================================================================
// Helpers
// ============================================================================

interface SectionHeadingProps {
  icon: IconName;
  children: React.ReactNode;
}

const SectionHeading: React.FC<SectionHeadingProps> = ({ icon, children }) => (
  <SectionTitle>
    <TitleIcon>
      <Icon name={icon} size="sm" aria-hidden />
    </TitleIcon>
    {children}
  </SectionTitle>
);

const Definitions: React.FC<{ items: readonly LabeledItem[] }> = ({
  items,
}) => (
  <DefinitionList>
    {items.map((item) => (
      <div key={item.label}>
        <dt>{item.label}</dt>
        <dd>{item.description}</dd>
      </div>
    ))}
  </DefinitionList>
);

// ============================================================================
// Component
// ============================================================================

export const TransparencyPage: React.FC = () => {
  const [isTermsOpen, setIsTermsOpen] = React.useState(false);

  return (
    <PageContainer>
      <TransparencyContainer>
        <PageHeader>
          <PageTitle>Transparencia</PageTitle>
          <PageSubtitle>
            Cómo calculamos tus indicadores de sostenibilidad, de dónde salen
            los factores de emisión y qué datos usamos para hacerlo.
          </PageSubtitle>
        </PageHeader>

        <GridContainer>
          <SectionCard>
            <SectionHeading icon="info">
              ¿Qué es nuestra plataforma?
            </SectionHeading>
            <SectionContent>
              <p>
                <strong>Foodprint by Baseline</strong> ayuda a restaurantes a
                medir, mejorar y comunicar su desempeño ambiental.
              </p>
              <p>
                Procesamos tus facturas de electricidad, gas y agua mediante
                OCR, sumamos tus registros de residuos y lo transformamos en
                indicadores claros que te permiten:
              </p>
              <BulletList>
                <li>Identificar oportunidades de eficiencia</li>
                <li>Reducir costes operativos</li>
                <li>Fijar y seguir metas de sostenibilidad</li>
                <li>Generar reportes para comunicar tu compromiso</li>
              </BulletList>
            </SectionContent>
          </SectionCard>

          <SectionCard>
            <SectionHeading icon="target">
              ¿Por qué medir por plato?
            </SectionHeading>
            <SectionContent>
              <p>
                Normalizar el consumo <strong>por plato servido</strong> separa
                la eficiencia real de la variación en la actividad del
                restaurante. Así puedes:
              </p>
              <Definitions
                items={[
                  {
                    label: 'Crear tu línea de base',
                    description:
                      'Comparar tu desempeño mes a mes e identificar tendencias.',
                  },
                  {
                    label: 'Fijar metas realistas',
                    description:
                      'Las metas parten de al menos 3 meses de datos de los últimos 12.',
                  },
                  {
                    label: 'Comunicar impacto',
                    description:
                      'Reportes con datos trazables para clientes, inversores o certificaciones.',
                  },
                ]}
              />
            </SectionContent>
          </SectionCard>

          <SectionCard $fullWidth>
            <SectionHeading icon="chart">¿Qué calculamos?</SectionHeading>
            <SectionContent>
              <p>
                Cada indicador se calcula sobre el total mensual y se divide por
                los platos servidos ese mes. Si un mes no tiene platos
                registrados, el indicador por plato queda vacío en lugar de
                mostrar un valor engañoso.
              </p>
              <IndicatorGrid>
                {INDICATORS.map((indicator) => (
                  <IndicatorItem key={indicator.title}>
                    <SubTitle>
                      <Icon name={indicator.icon} size="sm" aria-hidden />
                      {indicator.title}
                    </SubTitle>
                    <Formula>{indicator.formula}</Formula>
                    <p>{indicator.description}</p>
                  </IndicatorItem>
                ))}
              </IndicatorGrid>
            </SectionContent>
          </SectionCard>

          <SectionCard $fullWidth>
            <SectionHeading icon="eco">Factores de emisión</SectionHeading>
            <SectionContent>
              <p>
                Usamos factores <strong>oficiales de Argentina</strong> y
                seguimos el GHG Protocol para clasificar las emisiones. El CO₂e
                se agrega con los potenciales de calentamiento del IPCC AR5 (CH₄
                = 28, N₂O = 265).
              </p>

              <FactorsLayout>
                <FactorPanel>
                  <SubTitle id="electricity-factors-title">
                    <Icon name="electricity" size="sm" aria-hidden />
                    Electricidad de red · Alcance 2
                  </SubTitle>
                  <FactorTable aria-labelledby="electricity-factors-title">
                    <thead>
                      <tr>
                        <th scope="col">Año</th>
                        <th scope="col">kg CO₂e/kWh</th>
                        <th scope="col">Fuente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ELECTRICITY_FACTORS.map((row) => (
                        <tr key={row.year}>
                          <td>{row.year}</td>
                          <td>{row.factor}</td>
                          <td>
                            {row.source}
                            {row.note && <Muted> · {row.note}</Muted>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </FactorTable>
                  <p>
                    Se aplica el factor del año de cada factura. Desde 2024 la
                    fuente (CAMMESA) publica solo CO₂, por lo que esos años
                    pueden quedar ≈1 % por debajo.
                  </p>
                  <SourceNote>
                    Fuentes:{' '}
                    <a
                      href={SOURCE_LINKS.snigei}
                      target="_blank"
                      rel="noreferrer"
                    >
                      SNIGEI-AR, Tabla 5
                    </a>{' '}
                    ·{' '}
                    <a
                      href={SOURCE_LINKS.cammesa}
                      target="_blank"
                      rel="noreferrer"
                    >
                      CAMMESA, Informe Anual 2025
                    </a>
                  </SourceNote>
                </FactorPanel>

                <div>
                  <FactorPanel>
                    <SubTitle>
                      <Icon name="gas" size="sm" aria-hidden />
                      Gas natural · Alcance 1
                    </SubTitle>
                    <FactorValue>{GAS_FACTOR} kg CO₂e/m³</FactorValue>
                    <p>
                      Combustión en instalaciones fijas. Si tu factura viene en
                      kWh, convertimos con 9.300 kcal/m³ (estándar ENARGAS): 1
                      m³ ≈ 10,82 kWh.
                    </p>
                    <SourceNote>
                      Fuente:{' '}
                      <a
                        href={SOURCE_LINKS.snigei}
                        target="_blank"
                        rel="noreferrer"
                      >
                        SNIGEI-AR, Tabla 4
                      </a>
                    </SourceNote>
                  </FactorPanel>

                  <FactorPanel>
                    <SubTitle>
                      <Icon name="info" size="sm" aria-hidden />
                      Qué no incluye la huella
                    </SubTitle>
                    <p>
                      Agua, residuos, pérdidas de red y emisiones previas a la
                      generación (Alcance 3) no se incluyen en el CO₂e. Seguimos
                      midiendo agua y residuos como indicadores propios.
                    </p>
                  </FactorPanel>
                </div>
              </FactorsLayout>
            </SectionContent>
          </SectionCard>

          <SectionCard>
            <SectionHeading icon="file">Fuentes de datos</SectionHeading>
            <SectionContent>
              <Definitions items={DATA_SOURCES} />
              <InfoBox>
                <Icon name="search" size="sm" aria-hidden />
                <p>
                  <strong>¿Encontraste un error?</strong> Puedes revisar y
                  corregir los datos de cualquier factura procesada.
                </p>
              </InfoBox>
            </SectionContent>
          </SectionCard>

          <SectionCard>
            <SectionHeading icon="calendar">
              Cómo tratamos los datos
            </SectionHeading>
            <SectionContent>
              <Definitions items={CALCULATION_RULES} />
            </SectionContent>
          </SectionCard>

          <SectionCard>
            <SectionHeading icon="lock">Privacidad y seguridad</SectionHeading>
            <SectionContent>
              <p>
                <strong>Tu privacidad es nuestra prioridad.</strong> Tus datos
                se transmiten y almacenan de forma protegida.
              </p>
              <BulletList>
                {PRIVACY_ITEMS.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </BulletList>
            </SectionContent>
          </SectionCard>

          <SectionCard>
            <SectionHeading icon="clipboard">
              Términos y Condiciones
            </SectionHeading>
            <SectionContent>
              <p>
                Consulta los Términos y Condiciones vigentes en cualquier
                momento para saber cómo operamos la plataforma y tratamos tus
                datos.
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsTermsOpen(true)}
                aria-haspopup="dialog"
              >
                Ver Términos y Condiciones
              </Button>
            </SectionContent>
          </SectionCard>

          <SectionCard $fullWidth>
            <SectionHeading icon="layers">Próximas mejoras</SectionHeading>
            <SectionContent>
              <p>Trabajamos continuamente para mejorar la plataforma:</p>
              <IconList>
                {UPCOMING_ITEMS.map((item) => (
                  <li key={item.text}>
                    <Icon name={item.icon} size="sm" aria-hidden />
                    {item.text}
                  </li>
                ))}
              </IconList>
              <FooterNote>
                ¿Tienes preguntas sobre nuestras metodologías? Contáctanos desde
                la sección de soporte de tu perfil.
              </FooterNote>
            </SectionContent>
          </SectionCard>
        </GridContainer>
      </TransparencyContainer>

      <TermsModal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} />
    </PageContainer>
  );
};

export default TransparencyPage;
